import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { publicImageSchema, storefrontHostSchema } from "./storefront.js";
/**
 * DG-05 (docs/specs/digital.md, BK-01): physical or digital variants, the digital kinds, book details and authors.
 * A product is physical only, digital only, or both (a "Format" option with a hard copy and an eBook variant).
 */
export const FULFILMENTS = ["physical", "digital"];
export const fulfilmentSchema = z.enum(FULFILMENTS);
/** Digital kinds; later phases add `file`, `licence_key`, `link`, `course`. */
export const DIGITAL_KINDS = ["ebook"];
export const digitalKindSchema = z.enum(DIGITAL_KINDS);
/** Details any book has, hard copy or eBook. The publisher is the product's brand. */
export const bookDetailsSchema = z
    .object({
    isbn: z.string().trim().max(20).default(""),
    pages: z.int().min(1).max(100_000).nullable().default(null),
    edition: z.string().trim().max(60).default(""),
    language: z.string().trim().max(40).default(""),
    publishedYear: z.int().min(1000).max(2200).nullable().default(null),
})
    .strict();
export const AUTHOR_ROLES = ["author", "translator", "editor"];
export const authorRoleSchema = z.enum(AUTHOR_ROLES);
/** An author of a product, in order. */
export const productAuthorSchema = z.object({
    authorId: z.uuid(),
    name: z.string(),
    slug: z.string(),
    role: authorRoleSchema,
});
export const productAuthorInputSchema = z
    .object({ authorId: z.uuid(), role: authorRoleSchema.default("author") })
    .strict();
export const authorSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    bio: z.string(),
    photoFileId: z.uuid().nullable(),
    productCount: z.int(),
    version: z.int(),
});
const authorBody = z
    .object({
    name: z.string().trim().min(1).max(120),
    bio: z.string().trim().max(2000).default(""),
    photoFileId: z.uuid().nullable().default(null),
})
    .strict();
const tag = { tag: "catalog" };
const view = { permission: "catalog.view" };
const manage = { permission: "catalog.manage" };
export const listAuthors = defineEndpoint({
    ...tag,
    name: "listAuthors",
    method: "GET",
    path: "/v1/authors",
    access: view,
    summary: "Authors by name (search a part of the name)",
    query: z.object({ q: z.string().trim().max(100).optional() }),
    status: 200,
    response: z.object({ data: z.array(authorSchema) }),
});
export const createAuthor = defineEndpoint({
    ...tag,
    name: "createAuthor",
    method: "POST",
    path: "/v1/authors",
    access: manage,
    summary: "Add an author (its web address comes from the name)",
    body: authorBody,
    status: 201,
    response: authorSchema,
    errors: ["VALIDATION_FAILED"],
});
export const updateAuthor = defineEndpoint({
    ...tag,
    name: "updateAuthor",
    method: "PATCH",
    path: "/v1/authors/{id}",
    access: manage,
    summary: "Change an author's name, bio or photo",
    params: idParamsSchema,
    body: authorBody
        .partial()
        .extend({ version: z.int().min(1) })
        .strict(),
    status: 200,
    response: authorSchema,
    errors: ["NOT_FOUND", "CONFLICT"],
});
export const deleteAuthor = defineEndpoint({
    ...tag,
    name: "deleteAuthor",
    method: "DELETE",
    path: "/v1/authors/{id}",
    access: manage,
    summary: "Remove an author no product names any more",
    params: idParamsSchema,
    status: 204,
    errors: ["NOT_FOUND", "BUSINESS_RULE_VIOLATION"],
});
// The shop's authors (DG-08, BK-01) ------------------------------------------------------------------------------
export const shopAuthorSchema = z.object({
    name: z.string(),
    slug: z.string(),
    bio: z.string(),
    photo: publicImageSchema.nullable(),
    /** Published products with this author (any role). */
    bookCount: z.int(),
});
export const listShopAuthors = defineEndpoint({
    tag: "storefront",
    name: "listShopAuthors",
    method: "GET",
    path: "/v1/storefront/authors",
    access: { public: true },
    summary: "Authors with published books in the shop, most books first",
    query: z.object({
        host: storefrontHostSchema,
        limit: z.coerce.number().int().min(1).max(48).default(12),
    }),
    status: 200,
    response: z.object({ data: z.array(shopAuthorSchema) }),
});
export const getShopAuthor = defineEndpoint({
    tag: "storefront",
    name: "getShopAuthor",
    method: "GET",
    path: "/v1/storefront/authors/{slug}",
    access: { public: true },
    summary: "An author's page: name, photo, bio and how many of their books the shop has (books: products?author=)",
    params: z.object({ slug: z.string().trim().min(1).max(160) }),
    query: z.object({ host: storefrontHostSchema }),
    status: 200,
    response: shopAuthorSchema,
    errors: ["NOT_FOUND"],
});
//# sourceMappingURL=catalog-books.js.map